import React, { useState, useEffect } from 'react'
import _ from 'lodash'
import moment from 'moment'
import { useTranslation } from '~/hooks/useTranslation'
import { getApi } from '~/util/api'
import { Button, Card, Typography } from '~/components/_atoms'
import { Stack } from '~/components/_layouts'
import Spinner from '~/components/shared/Spinner'
import { useBenefits } from '../../coach_services/BenefitsProvider'
import ScheduleDialog from '../../coach_services/ProfessionalServices/ScheduleDialog'
import { useFormatTime } from '~/hooks/useFormatTime'
import UpdateCandidateProfile from '~/components/UpdateCandidateProfile'
import HeadshotCircle from '~/components/coach_services/MeetYourCoach/HeadshotCircle'

export function PremiumCoachCard({ className }) {
  const [isLoading, setIsLoading] = useState(false)
  const [coachInfo, setCoachInfo] = useState(null)
  useEffect(() => {
    setIsLoading(true)
    getApi()
      .get('/coach_info')
      .then((response) => {
        if (response.status == 200) {
          setCoachInfo(response.data)
          return
        } else {
          throw new Error('json request failed')
        }
      })
      .finally(() => {
        setIsLoading(false)
      })
      .catch((error) => {
        console.log('json request failed', error)
      })
  }, [setCoachInfo])

  return isLoading || _.isEmpty(coachInfo) ? (
    <div className="flex items-center justify-center">
      <Spinner show className="h-9 w-9" />
    </div>
  ) : (
    <>
      <DedicatedCoachCard className={className} coach={coachInfo} />
    </>
  )
}

function DedicatedCoachCard({ className, coach }) {
  const { t, locale } = useTranslation()
  const { current_user, serviceRequestList, select } = useBenefits()
  const [coachState, setCoachState] = useState('no_service_requests')
  const [orientationCall, setOrientationCall] = useState(null)

  const [nextServiceRequest, setNextServiceRequest] = useState(null)
  const [showUpdateProfileDialog, setShowUpdateProfileDialog] = useState(false)
  const formatTime = useFormatTime(locale)

  useEffect(() => {
    // Use service_status from coachInfo if available, otherwise fall back to default
    if (coach?.service_status) {
      setCoachState(coach.service_status)
    } else {
      setCoachState('default')
    }

    // Keep the existing logic for extracting service request data for display purposes
    const orientationCall =
      serviceRequestList['ServiceRequest::OrientationCall']
    setOrientationCall(orientationCall)
    // Find next service request for display purposes
    const nextRequest = Object.entries(serviceRequestList)
      .filter(
        ([key, request]) =>
          key !== 'ServiceRequest::OrientationCall' && !request.completed,
      )
      .map(([_, request]) => request)
      .filter((request) => {
        if (request.calendly_event) {
          return moment(request.calendly_event.end_time).isAfter(moment())
        } else {
          return request.due_at && moment(request.due_at).isAfter(moment())
        }
      })
      .sort((a, b) => moment(a.due_at).diff(moment(b.due_at)))[0]
    setNextServiceRequest(nextRequest)
  }, [coach, serviceRequestList])

  const toggleShowUpdateProfileDialog = () => {
    setShowUpdateProfileDialog(!showUpdateProfileDialog)
    getApi()
      .get('/coach_info')
      .then((response) => {
        if (response.status == 200) {
          setCoachState(response.data?.service_status || 'default')
          return
        } else {
          throw new Error('json request failed')
        }
      })
      .catch((error) => {
        console.log('json request failed', error)
      })
  }

  const renderActions = () => {
    switch (coachState) {
      case 'liaison_noinfo':
        return (
          <div className="flex w-full flex-col gap-4 md:flex-row">
            <UpdateCandidateProfile
              selectProgram={select}
              showUpdateProfileDialog={showUpdateProfileDialog}
              toggleShowUpdateProfileDialog={toggleShowUpdateProfileDialog}
            />
            <Button
              color="secondary"
              onClick={() => toggleShowUpdateProfileDialog()}
            >
              {t('premium_liason_coach_card.update_candidate_profile')}
            </Button>
            <Button
              color="secondary"
              variant="outline"
              href="/coach_services/meet_your_coach"
            >
              {t('premium_liason_coach_card.view_action')}
            </Button>
          </div>
        )
      case 'liaison':
        return (
          <div className="grid w-full grid-cols-1 gap-4 @md:grid-cols-2">
            <Button color="secondary" href="/coach_services/meet_your_coach">
              {t('premium_liason_coach_card.meet_liaison')}
            </Button>
            <Button
              color="secondary"
              variant="outline"
              href="/coach_services/professional_services"
            >
              {t('premium_liason_coach_card.professional_services')}
            </Button>
          </div>
        )
      case 'orientation_call_scheduled':
        return (
          <div className="grid w-full grid-cols-1 gap-4 @lg:grid-cols-2">
            <Button
              href={orientationCall?.calendly_event?.join_url}
              target="_blank"
              rel="noopener noreferrer"
              variant="solid"
              color="secondary"
            >
              {t(
                'premium_dedicated_coach_card.orientation_call_scheduled.join_meeting',
              )}
            </Button>
            <Button
              key="coach-services"
              href="/coach_services/professional_services/orientation_call"
              variant="outline"
              color="secondary"
            >
              {t(
                'premium_dedicated_coach_card.orientation_call_scheduled.view_details',
              )}
            </Button>
          </div>
        )
      case 'no_service_requests':
        return (
          <div className="grid w-full grid-cols-1 gap-4 @lg:grid-cols-2">
            <Button
              key="meet-your-coach"
              href="/coach_services/meet_your_coach"
              variant="outline"
              color="secondary"
            >
              {t(
                'premium_dedicated_coach_card.no_service_requests.meet_your_coach',
              )}
            </Button>
            <Button
              key="schedule-meeting"
              href="/coach_services/professional_services"
              variant="solid"
              color="secondary"
            >
              {t(
                'premium_dedicated_coach_card.no_service_requests.professional_services',
              )}
            </Button>
          </div>
        )
      case 'service_request_scheduled':
        return nextServiceRequest.meeting_via_calendly ? (
          <div className="grid w-full grid-cols-1 gap-4 @lg:grid-cols-2">
            <Button
              href={nextServiceRequest?.calendly_event?.join_url}
              target="_blank"
              rel="noopener noreferrer"
              variant="solid"
              color="secondary"
            >
              {t(
                'premium_dedicated_coach_card.service_request_scheduled.join_meeting',
              )}
            </Button>
            <Button
              key="coach-services"
              href="/coach_services/professional_services"
              variant="outline"
              color="secondary"
            >
              {t('premium_dedicated_coach_card.coach_services')}
            </Button>
          </div>
        ) : (
          <div className="grid w-full grid-cols-1 gap-4 @lg:grid-cols-2">
            <Button
              href={
                nextServiceRequest.type === 'resume_review'
                  ? 'coach_services/resume_review'
                  : `/coach_services/professional_services/${nextServiceRequest.type}`
              }
              variant="solid"
              color="secondary"
            >
              {t(
                'premium_dedicated_coach_card.service_request_scheduled.view_details',
              )}
            </Button>
            <Button
              key="coach-services"
              href="/coach_services/professional_services"
              variant="outline"
              color="secondary"
            >
              {t('premium_dedicated_coach_card.coach_services')}
            </Button>
          </div>
        )

      case 'service_request_feedback':
        return (
          <div className="grid w-full grid-cols-1 gap-4 @lg:grid-cols-2">
            <Button
              href="/coach_services/feedback"
              variant="solid"
              color="secondary"
            >
              {t(
                'premium_dedicated_coach_card.service_request_feedback.view_feedback',
              )}
            </Button>
            <Button
              key="coach-services"
              href="/coach_services/professional_services"
              variant="outline"
              color="secondary"
            >
              {t('premium_dedicated_coach_card.coach_services')}
            </Button>
          </div>
        )

      case 'service_request_feedback_including_orientation':
        return (
          <div className="grid w-full grid-cols-1 gap-4 @lg:grid-cols-2">
            <Button
              href="/coach_services/professional_services"
              variant="solid"
              color="secondary"
            >
              {t(
                'premium_dedicated_coach_card.service_request_feedback_including_orientation.view_feedback',
              )}
            </Button>
          </div>
        )

      case 'completed_service_requests':
        return (
          <div className="grid w-full grid-cols-1 gap-4 @lg:grid-cols-2">
            <Button
              key="coach-services"
              href="/coach_services/professional_services"
              variant="solid"
              color="secondary"
            >
              {t('premium_dedicated_coach_card.view_coach_services')}
            </Button>
            <Button
              key="message-your-coach"
              className="w-full"
              onClick={() => {
                window.location.href = '/coach_messages'
              }}
              variant="outline"
              color="secondary"
            >
              {t(
                'premium_dedicated_coach_card.completed_service_requests.message_your_coach',
              )}
            </Button>
          </div>
        )
      case 'cancelled_by_coach_change':
        return (
          <div className="grid w-full grid-cols-1 gap-4 @lg:grid-cols-2">
            <Button
              href={`/coach_services/professional_services/`}
              variant="solid"
              color="secondary"
            >
              {t(
                'premium_dedicated_coach_card.service_request_cancelled.reschedule',
              )}
            </Button>
            <Button
              key="meet-your-coach"
              href="/coach_services/meet_your_coach"
              variant="outline"
              color="secondary"
            >
              {t(
                'premium_dedicated_coach_card.orientation_call_not_scheduled.meet_your_coach',
              )}
            </Button>
          </div>
        )
      // should only happen for resume review service requests
      case 'needs_scheduling':
        return (
          <div className="grid w-full grid-cols-1 gap-4 @lg:grid-cols-2">
            <Button
              href={'coach_services/resume_review'}
              variant="solid"
              color="secondary"
            >
              {t(
                'premium_dedicated_coach_card.service_request_scheduled.view_details',
              )}
            </Button>
            <Button
              key="coach-services"
              href="/coach_services/professional_services"
              variant="outline"
              color="secondary"
            >
              {t('premium_dedicated_coach_card.coach_services')}
            </Button>
          </div>
        )
      case 'service_request_cancelled':
        return (
          <div className="grid w-full grid-cols-1 gap-4 @lg:grid-cols-2">
            <Button
              href={`/coach_services/professional_services/`}
              variant="solid"
              color="secondary"
            >
              {t(
                'premium_dedicated_coach_card.service_request_cancelled.reschedule',
              )}
            </Button>
            <Button
              key="message-your-coach"
              className="w-full"
              onClick={() => {
                window.location.href = '/coach_messages'
              }}
              variant="outline"
              color="secondary"
            >
              {t(
                'premium_dedicated_coach_card.service_request_cancelled.message_your_coach',
              )}
            </Button>
          </div>
        )

      case 'intro_complete_no_new_feedback_no_other_progress':
        return (
          <div className="grid w-full grid-cols-1 gap-4 @lg:grid-cols-2">
            <Button
              key="coach-services"
              href="/coach_services/professional_services"
              variant="solid"
              color="secondary"
            >
              {t('premium_dedicated_coach_card.go_to_professional_services')}
            </Button>
            <Button
              key="message-your-coach"
              className="w-full"
              onClick={() => {
                window.location.href = '/coach_messages'
              }}
              variant="outline"
              color="secondary"
            >
              {t(
                'premium_dedicated_coach_card.completed_service_requests.message_your_coach',
              )}
            </Button>
          </div>
        )

      default:
        return (
          <div className="grid w-full grid-cols-1 gap-4 @lg:grid-cols-2">
            <Button
              key="coach-services"
              href="/coach_services/professional_services"
              variant="solid"
              color="secondary"
            >
              {t('premium_dedicated_coach_card.coach_services')}
            </Button>
            <Button
              key="meet-your-coach"
              href="/coach_services/meet_your_coach"
              variant="outline"
              color="secondary"
            >
              {t(
                'premium_dedicated_coach_card.orientation_call_not_scheduled.meet_your_coach',
              )}
            </Button>
          </div>
        )
    }
  }

  const renderDescription = () => {
    switch (coachState) {
      case 'liaison_noinfo':
        return t('premium_liason_coach_card.description_noinfo', {
          first_name: current_user.first_name,
        })
      case 'liaison':
        return t('premium_liason_coach_card.we_are_working', {
          first_name: current_user.first_name,
        })
      case 'orientation_call_scheduled':
        return t(
          'premium_dedicated_coach_card.orientation_call_scheduled.description',
          {
            meeting_time: formatTime(
              orientationCall?.calendly_event?.start_time,
              'meeting_datetime',
            ),
          },
        )
      case 'no_service_requests':
        return t(
          'premium_dedicated_coach_card.no_service_requests.great_news',
          { coach_name: coach.first_name },
        )
      case 'service_request_feedback': {
        return t(
          'premium_dedicated_coach_card.service_request_feedback.description',
          {
            service_name:
              coach?.most_recent_unviewed_completed_service_request
                ?.calendly_event?.name,
          },
        )
      }

      case 'service_request_feedback_including_orientation': {
        return t(
          'premium_dedicated_coach_card.service_request_feedback_including_orientation.description',
        )
      }
      case 'cancelled_by_coach_change':
        return t(
          'premium_dedicated_coach_card.service_request_cancelled.coach_change',
          {
            coach_name: coach?.name,
          },
        )
      case 'needs_scheduling':
        return t(
          'premium_dedicated_coach_card.service_request_scheduled.needs_scheduling',
        )
      case 'service_request_cancelled':
        return t(
          'premium_dedicated_coach_card.service_request_cancelled.were_sorry',
          {
            coach_name: coach?.name,
          },
        )
      case 'service_request_scheduled':
        return nextServiceRequest?.meeting_via_calendly
          ? t(
              'premium_dedicated_coach_card.service_request_scheduled.description',
              {
                service_name: nextServiceRequest?.name,
                meeting_time: `${formatTime(
                  nextServiceRequest?.calendly_event?.start_time,
                  'date',
                )} at ${formatTime(nextServiceRequest?.calendly_event?.start_time, 'time')}`,
              },
            )
          : t(
              'premium_dedicated_coach_card.service_request_scheduled.description_no_meeting',
              {
                service_name: nextServiceRequest?.name,
                receiver:
                  nextServiceRequest?.name === 'Target Company Research'
                    ? t(
                        'premium_dedicated_coach_card.service_request_scheduled.intoo_research_team',
                      )
                    : t(
                        'premium_dedicated_coach_card.service_request_scheduled.your_coach',
                      ),
                due_at: formatTime(nextServiceRequest?.due_at, 'short'),
              },
            )
      case 'completed_service_requests':
        return t(
          'premium_dedicated_coach_card.completed_service_requests.description',
          {
            service_name:
              coach?.most_recent_completed_and_viewed_service_request
                ?.calendly_event?.name ||
              coach?.most_recent_completed_and_viewed_service_request
                ?.service_request?.content?.message,
          },
        )

      case 'intro_complete_no_new_feedback_no_other_progress':
        return t('premium_dedicated_coach_card.additional_services_available')

      default:
        return t(
          'premium_dedicated_coach_card.orientation_call_not_scheduled.description',
        )
    }
  }

  const renderCoachImage = () => {
    if (coach.liaison) {
      return (
        <HeadshotCircle
          coachImage={`https://www-yns-com.s3.amazonaws.com/images/coach_services/liaison_team.jpg`}
          variant="dashboard"
        />
      )
    } else if (coach?.headshot) {
      return <HeadshotCircle coachImage={coach?.headshot} variant="dashboard" />
    } else {
      return <HeadshotCircle coachImage={coach?.image} variant="dashboard" />
    }
  }

  return (
    <Card
      className={className}
      actions={renderActions()}
      data-service-status={coachState}
    >
      <Stack className="h-full justify-between" spacing={3}>
        <div className="flex flex-col gap-4 @md:flex-row">
          {renderCoachImage()}
          <Stack spacing={3}>
            <Typography variant="title4" color="secondary" component="h2">
              {coach.liaison ? t('premium_liason_coach_card.name') : coach.name}
            </Typography>
            {renderDescription()}
          </Stack>
        </div>
      </Stack>
      <ScheduleDialog current_user={current_user} coach={coach} />
    </Card>
  )
}
